//npm
import { LOCATION_INITIALIZED } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, } from '@angular/common/http';
import { inject, Injector, NgModule, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';

//own modules
import { AlertModule } from '@bayclubs/alert';
import { DropDownModule } from '@bayclubs/drop-down';
import { BcEmployeeAuthEnvironment, BcEmployeeAuthModule } from '@bayclubs/employee-auth';
import {
    AppVersionFooter,
    isDeployedRuntimeEnvironment,
    lightPreset,
    provideAuth,
    provideHorizonLogging,
    provideHorizonObservability,
    providePrimeng,
    provideVersionCheck,
} from '@bayclubs/horizon';
import { LoggingInterceptor } from '@bayclubs/logging';
import { BASE_PATH as PAYMENTS_PROCESSOR_OFFICE_BASE_PATH } from '@bayclubs/payments-processor-office-api';
import { SpinnerModule } from '@bayclubs/spinner';
import { BayclubsUiCoreModule } from '@bayclubs/ui-core';
import { BayclubsUiDatePickerModule } from '@bayclubs/ui-date-picker';
import { LoaderInterceptor, UtilsModule } from '@bayclubs/utils';

import { provideTranslateService, TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsRouterPluginModule } from '@ngxs/router-plugin';
import { NgxsModule } from '@ngxs/store';
import { AccordionModule } from 'ngx-bootstrap/accordion';
import { ModalModule } from 'ngx-bootstrap/modal';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { UiSwitchModule } from 'ngx-ui-switch';
import { NgxUploaderModule } from 'ngx-uploader';

//application components
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';

//routes
import { appRoutes } from './app.routes';

//application modules
import { CoreModule } from './core/core.module';
import { InfrastructureModule } from './infrastructure/infrastructure.module';
import { ErrorInterceptor } from './infrastructure/services/api/error.interceptor';

// MSAL matches protected resources by URL prefix, so a trailing slash would produce a '//*'
// pattern that never matches the requests the generated clients actually send.
const protectedResource = (url: string) => url.replace(/\/+$/, '') + '/*';

function buildFaroEndpoint(envName: string): string {
    return `https://faro.${envName === 'prod' ? '' : envName + '.'}bayclubs.io/collect`;
}

@NgModule({
    declarations: [ AppComponent ],
    bootstrap: [ AppComponent ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        RouterModule.forRoot(appRoutes),
        FormsModule,
        NgxsModule.forRoot([], { developmentMode: !environment.production }),
        ReactiveFormsModule,
        UtilsModule.forRoot(),
        BcEmployeeAuthModule.forRoot({
            accessDeniedRoute: '/forbidden',
            loginRoute: '/login',
            adClientId: environment.adClientId,
            adTenant: environment.adTenant,
            authenticationBaseUrl: environment.authenticationBaseUrl,
            baseHref: environment.baseHref,
            redirectUri: window.location.origin + environment.baseHref + 'code',
        } as BcEmployeeAuthEnvironment),
        TranslateDirective,
        TranslatePipe,
        CoreModule,
        InfrastructureModule,
        TabsModule,
        ModalModule,
        AccordionModule,
        TooltipModule,
        UiSwitchModule,
        NgxUploaderModule,
        AlertModule.forRoot(),
        SpinnerModule.forRoot(),
        DropDownModule.forRoot(),
        BayclubsUiCoreModule.forRoot(),
        BayclubsUiDatePickerModule,
        NgxsRouterPluginModule.forRoot(),
        NgxsReduxDevtoolsPluginModule.forRoot({
            disabled: environment.production,
        }),
        AppVersionFooter
    ],
    providers: [
        { provide: 'AuthEnvironment', useValue: environment },
        { provide: 'options', useValue: {} }, //after angular 6 upgrade it is required - check why(propably other dependency require it)
        // @bayclubs/employee-auth injects AppLogService (@bayclubs/logging) internally and needs
        // this token even though the app's own logging now goes through provideHorizonLogging below.
        {
            provide: 'loggingConfig',
            useValue: {
                consoleLogEnabled: !environment.production,
                seqLoggingEndpoint: environment.seqLoggingEndpoint,
                seqLoggingApiKey: environment.seqLoggingApiKey,
                appVersion: environment.appVersion,
            },
        },
        provideAppInitializer(() => {
            const initializerFn = (appInitializerFactory)(inject(TranslateService), inject(Injector));
            return initializerFn();
        }),
        provideAuth({
            config: {
                clientId: environment.adClientId,
                tenantId: environment.adTenant,
                redirectUri: environment.adRedirectUri,
                protectedResources: {
                    [protectedResource(environment.apiBaseUrl)]: environment.officeApiScope,
                    [protectedResource(environment.paymentsProcessorOfficeApiUrl)]: environment.paymentsProcessorOfficeApiScope,
                },
                loginFailedRoute: '/login',
                returnUrlStrategy: 'remember',
            },
        }),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LoggingInterceptor,
            multi: true,
        },
        { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LoaderInterceptor,
            multi: true,
        },
        {
            provide: PAYMENTS_PROCESSOR_OFFICE_BASE_PATH,
            useValue: environment.paymentsProcessorOfficeApiUrl,
        },
        provideHttpClient(withInterceptorsFromDi()),
        provideTranslateService({
            loader: provideTranslateHttpLoader({
                prefix: 'localization/',
                suffix: `.json?timestamp=${new Date().getTime()}`,
            }),
        }),
        provideServiceWorker('ngsw-worker.js', { enabled: isDeployedRuntimeEnvironment() }),
        providePrimeng(lightPreset),
        provideVersionCheck(),
        provideHorizonLogging({
            config: {
                appVersion: environment.appVersion,
                console: {
                    enabled: !environment.production,
                },
                seq: {
                    enabled: !!environment.seqLoggingEndpoint,
                    endpoint: environment.seqLoggingEndpoint,
                    apiKey: environment.seqLoggingApiKey,
                },
                observability: {
                    enabled: environment.observabilityEnabled,
                    endpoint: buildFaroEndpoint(environment.environment),
                    serviceName: environment.observabilityServiceName,
                    serviceNamespace: environment.observabilityServiceNamespace,
                    environment: environment.environment,
                },
            },
        }),
        provideHorizonObservability()
    ],
})
export class AppModule {
}

export function appInitializerFactory(
    translate: TranslateService,
    injector: Injector
) {
    return () =>
        new Promise<any>((resolve: any) => {
            const locationInitialized = injector.get(
                LOCATION_INITIALIZED,
                Promise.resolve(null)
            );

            locationInitialized.then(() => {
                const langToSet = 'locale-en';
                translate.setFallbackLang(langToSet);
                translate.use(langToSet).subscribe(
                    () => {
                        console.info(
                            `Successfully initialized '${langToSet}' language.'`
                        );
                    },
                    (err) => {
                        console.log(err);
                        console.error(
                            `Problem with '${langToSet}' language initialization.'`
                        );
                    },
                    () => {
                        resolve(null);
                    }
                );
            });
        });
}
